import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Input,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import { categoryService, contentService, CategoryDisplayOrderGetResponse } from '../services';
import { Category, Content } from '../types';
import CategoryForm from '../components/CategoryForm';
import BreadCrumb from '../components/Common/BreadCrumb';
import DeleteModal from '../components/Common/DeleteModal';
import ContentDetailModal from '../components/Common/ContentDetailModal';
import SortableVerticalList from '../components/Common/SortableVerticalList';
import { toast } from 'react-toastify';
import { formatUkDate } from '../utils/dateFormat';

const ContentPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Categories state
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [viewMode, setViewMode] = useState<'subcategories' | 'content'>('content'); // Track what to show in table
  
  // Content state
  const [contents, setContents] = useState<Content[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [totalContent, setTotalContent] = useState(0);
  const [itemsPerPage] = useState(10);
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const searchTerm = searchParams.get('search') || '';
  const contentTypeFilter = (searchParams.get('type') || 'all') as 'all' | 'single' | 'module';
  
  // UI state
  const [error, setError] = useState('');
  const [categoryModal, setCategoryModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [orderModal, setOrderModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [viewingContent, setViewingContent] = useState<Content | null>(null);
  const [orderedItems, setOrderedItems] = useState<{ _id: string; title: string }[]>([]);
  const [orderMode, setOrderMode] = useState<CategoryDisplayOrderGetResponse['mode'] | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; type: 'category' | 'content'; id: string; name: string }>({
    show: false,
    type: 'content',
    id: '',
    name: '',
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (!categoryId) {
      setSelectedCategory(null);
      return;
    }
    // If we already have the right category loaded, skip the lookup
    if (selectedCategory?._id === categoryId) return;
    // Try to resolve from already-loaded categories list
    const found = categories.find(c => c._id === categoryId);
    if (found) {
      setSelectedCategory(found);
    } else {
      // categories not loaded yet — will re-run once loadCategories populates them
    }
  }, [categoryId, categories]);

  useEffect(() => {
    if (selectedCategory) {
      const isParentCategory = !selectedCategory.parentId ||
        (typeof selectedCategory.parentId === 'object' && !selectedCategory.parentId);

      if (isParentCategory) {
        setViewMode('subcategories');
        loadSubcategories();
        loadContent();
      } else {
        setViewMode('content');
        loadContent();
      }
    }
  }, [selectedCategory, currentPage, searchTerm, contentTypeFilter]);

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      // Fetch all categories with high limit so all parents + subcategories load (sidebar and table)
      const response = await categoryService.getAll(1, 500);
      const data = Array.isArray(response) ? response : response.data || [];
      setCategories(data);
      setError('');
    } catch (err: any) {
      setError('Failed to load categories');
      toast.error('Failed to load categories');
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadSubcategories = async () => {
    if (!selectedCategory) return;

    const isParent = !selectedCategory.parentId ||
      (typeof selectedCategory.parentId === 'object' && !selectedCategory.parentId);

    setLoadingContent(true);
    try {
      const response = await categoryService.getChildren(selectedCategory._id, currentPage, itemsPerPage);

      if (response.children && Array.isArray(response.children)) {
        setSubcategories(response.children);
        if (!isParent) setTotalContent(response.pagination?.total || response.children.length);
      } else if (response.data && Array.isArray(response.data)) {
        setSubcategories(response.data);
        if (!isParent) setTotalContent(response.total || response.data.length);
      } else if (Array.isArray(response)) {
        setSubcategories(response);
        if (!isParent) setTotalContent(response.length);
      } else {
        setSubcategories([]);
        if (!isParent) setTotalContent(0);
      }

      setError('');
    } catch (err: any) {
      setError('Failed to load subcategories');
      toast.error('Failed to load subcategories');
      setSubcategories([]);
    } finally {
      setLoadingContent(false);
    }
  };

  const loadContent = async () => {
    if (!selectedCategory) return;
    
    setLoadingContent(true);
    try {
      const params: any = {
        categoryId: selectedCategory._id,
        page: currentPage,
        limit: itemsPerPage,
      };
      
      if (searchTerm) params.search = searchTerm;
      if (contentTypeFilter !== 'all') params.contentType = contentTypeFilter;
      
      const response = await contentService.getAll(params);
      
      // Handle paginated response
      if (response.data && Array.isArray(response.data)) {
        setContents(response.data);
        setTotalContent(response.pagination?.total ?? response.total ?? response.data.length);
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response
        const filtered = response.filter((content: Content) =>
          content.categories.includes(selectedCategory._id) &&
          (searchTerm ? content.title.toLowerCase().includes(searchTerm.toLowerCase()) : true)
        );
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);
        setContents(paginatedData);
        setTotalContent(filtered.length);
      } else {
        setContents([]);
        setTotalContent(0);
      }
      
      setError('');
    } catch (err: any) {
      setError('Failed to load content');
      toast.error('Failed to load content');
      setContents([]);
    } finally {
      setLoadingContent(false);
    }
  };

  const loadCategoryContentOrder = async (categoryId: string) => {
    setLoadingOrder(true);
    try {
      const res = await categoryService.getCategoryContentOrder(categoryId);
      setOrderMode(res.mode);
      if (res.mode === 'subcategories') {
        setOrderedItems(
          Array.isArray(res.items)
            ? res.items.map((it) => ({ _id: it._id, title: it.name }))
            : [],
        );
      } else {
        setOrderedItems(Array.isArray(res.items) ? res.items : []);
      }
    } catch {
      setOrderMode(null);
      setOrderedItems([]);
      toast.error('Failed to load category order');
    } finally {
      setLoadingOrder(false);
    }
  };

  // Old structure: filter to parents only
  const parentCategories = useMemo(() => {
    return categories.filter(cat => {
      if (!cat.parentId) return true;
      if (typeof cat.parentId === 'object' && cat.parentId !== null) return false;
      return false;
    });
  }, [categories]);

  const getSubcategories = useCallback((parentId: string) => {
    return categories.filter(cat => {
      if (!cat.parentId) return false;
      if (typeof cat.parentId === 'object' && cat.parentId !== null) {
        return (cat.parentId as any)._id === parentId;
      }
      return cat.parentId === parentId;
    });
  }, [categories]);

  // Pagination: compact window (1, ..., current±2, ..., last)
  const totalPages = Math.ceil(totalContent / itemsPerPage);
  const getVisiblePages = (): (number | 'ellipsis')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const show = new Set<number>([1, totalPages]);
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) show.add(i);
    const sorted = Array.from(show).sort((a, b) => a - b);
    const out: (number | 'ellipsis')[] = [];
    sorted.forEach((p, i) => {
      if (i > 0 && p - sorted[i - 1] > 1) out.push('ellipsis');
      out.push(p);
    });
    return out;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setSearchParams(prev => { prev.set('page', String(page)); return prev; }, { replace: true });
    }
  };

  // CRUD handlers
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryModal(true);
  };

  const handleAddSubcategory = () => {
    // Create a partial category object with parentId set
    setEditingCategory({ parentId: selectedCategory?._id } as Category);
    setCategoryModal(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryModal(true);
  };

  const handleAddContent = () => {
    if (!selectedCategory) {
      toast.warning('Please select a category first');
      return;
    }
    navigate('/dashboard/content/add', {
      state: { selectedCategoryId: selectedCategory._id, selectedCategory },
    });
  };

  const handleEditContent = (content: Content) => {
    navigate(`/dashboard/content/edit/${content._id}`, {
      state: selectedCategory
        ? { selectedCategoryId: selectedCategory._id, selectedCategory }
        : undefined,
    });
  };

  const handleViewContent = async (content: Content) => {
    setViewingContent(content);
    setDetailModal(true);
    try {
      const full = await contentService.getOne(content._id);
      setViewingContent(full);
    } catch {
      // Keep list item data if full fetch fails
    }
  };

  const handleOpenOrderModal = async () => {
    if (!selectedCategory) {
      toast.warning('Please select a category first');
      return;
    }
    setOrderModal(true);
    await loadCategoryContentOrder(selectedCategory._id);
  };

  const handleReorderIds = (nextIds: string[]) => {
    const byId = new Map(orderedItems.map((x) => [x._id, x]));
    setOrderedItems(nextIds.map((id) => byId.get(id)).filter(Boolean) as { _id: string; title: string }[]);
  };

  const handleSaveOrder = async () => {
    if (!selectedCategory) return;
    setSavingOrder(true);
    try {
      if (orderMode === 'subcategories') {
        await categoryService.updateCategoryDisplayOrder(selectedCategory._id, {
          subcategoryIds: orderedItems.map((x) => x._id),
        });
        toast.success('Subcategory order saved.');
      } else if (orderMode === 'content') {
        await categoryService.updateCategoryDisplayOrder(selectedCategory._id, {
          contentIds: orderedItems.map((x) => x._id),
        });
        toast.success('Content order saved.');
      } else {
        toast.error('Nothing to save');
        return;
      }
      setOrderModal(false);
      loadContent();
      loadCategories();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleClearOrder = async () => {
    if (!selectedCategory) return;
    setSavingOrder(true);
    try {
      if (orderMode === 'subcategories') {
        await categoryService.updateCategoryDisplayOrder(selectedCategory._id, { subcategoryIds: [] });
        toast.success('Subcategory order reset (by name).');
      } else if (orderMode === 'content') {
        await categoryService.updateCategoryDisplayOrder(selectedCategory._id, { contentIds: [] });
        toast.success('Custom content order cleared.');
      }
      await loadCategoryContentOrder(selectedCategory._id);
      loadContent();
      loadCategories();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to clear order');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleEditFromDetail = () => {
    if (viewingContent) {
      setDetailModal(false);
      navigate(`/dashboard/content/edit/${viewingContent._id}`, {
        state: selectedCategory
          ? { selectedCategoryId: selectedCategory._id, selectedCategory }
          : undefined,
      });
    }
  };

  const handleDeleteCategory = (category: Category) => {
    setDeleteModal({
      show: true,
      type: 'category',
      id: category._id,
      name: category.name,
    });
  };

  const handleDeleteContent = (content: Content) => {
    setDeleteModal({
      show: true,
      type: 'content',
      id: content._id,
      name: content.title,
    });
  };

  const confirmDelete = async () => {
    try {
      if (deleteModal.type === 'category') {
        // Optimistic update: Remove from local state immediately
        const deletedId = deleteModal.id;
        
        // Remove from categories list
        setCategories(prev => prev.filter(cat => cat._id !== deletedId));
        
        // If in subcategories view, remove from subcategories list
        if (viewMode === 'subcategories') {
          setSubcategories(prev => prev.filter(cat => cat._id !== deletedId));
          setTotalContent(prev => Math.max(0, prev - 1));
        }
        
        // Call API to delete
        await categoryService.delete(deletedId);
        
        toast.success('Category deleted successfully');
        
        // If deleted category was selected, clear selection
        if (selectedCategory?._id === deletedId) {
          navigate('/dashboard/content');
        }
      } else {
        // Optimistic update for content
        setContents(prev => prev.filter(content => content._id !== deleteModal.id));
        setTotalContent(prev => Math.max(0, prev - 1));
        
        await contentService.delete(deleteModal.id);
        toast.success('Content deleted successfully');
      }
    } catch (err: any) {
      toast.error(`Failed to delete ${deleteModal.type}`);
      // Reload data on error to restore correct state
      if (deleteModal.type === 'category') {
        loadCategories();
        if (viewMode === 'subcategories') {
          loadSubcategories();
        }
      } else {
        loadContent();
      }
    } finally {
      setDeleteModal({ show: false, type: 'content', id: '', name: '' });
    }
  };

  const handleCategorySaved = () => {
    setCategoryModal(false);
    
    // Reload categories in sidebar
    loadCategories();
    
    // If we're in subcategories view, reload the subcategories table
    if (viewMode === 'subcategories' && selectedCategory) {
      loadSubcategories();
    }
    
    toast.success(editingCategory?._id ? 'Category updated successfully' : 'Category created successfully');
  };

  const getStatusBadge = (status: string) => {
    const colors: { [key: string]: string } = {
      published: 'success',
      draft: 'warning',
      archived: 'secondary',
    };
    return <Badge color={colors[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {selectedCategory && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <BreadCrumb  
                pageTitle={selectedCategory.parentId ? (selectedCategory.parentId as any).name : 'Content Management'} 
              />
              <div className="d-flex gap-2">
                {viewMode === 'subcategories' && (
                  <Button color="success" onClick={handleAddSubcategory}>
                    <i className="ri-add-circle-line me-1"></i>
                    Add Subcategory
                  </Button>
                )}  
              </div>
            </div>
          )}
          
          {!selectedCategory && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <BreadCrumb pageTitle="Content Management" />
                <Button color="success" onClick={handleAddCategory} className="ms-3">
                  <i className="ri-add-circle-line me-1"></i>
                  Add Parent Category
                </Button>
              </div>
            </>
          )}

          {error && (
            <Alert color="danger" toggle={() => setError('')}>
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </Alert>
          )}

          {/* Full Width Content */}
          {selectedCategory ? (
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h5 className="card-title mb-1 text-truncate" title={selectedCategory.name}>
                      {selectedCategory.name}
                    </h5>
                    {selectedCategory.shortDesc && (
                      <p className="text-muted mb-0 fs-6 text-truncate" title={selectedCategory.shortDesc}>
                        {selectedCategory.shortDesc}
                      </p>
                    )}
                    {viewMode === 'subcategories' && (
                      <Badge color="info" className="mt-1">
                        <i className="ri-folder-line me-1"></i>
                        Parent Category
                      </Badge>
                    )}
                  </div>
                  <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                    <Button color="secondary" outline onClick={handleOpenOrderModal}>
                      <i className="ri-draggable me-1"></i>
                      Set Order (Drag & Drop)
                    </Button>
                    <Button color="primary" onClick={handleAddContent}>
                      <i className="ri-add-line me-1"></i>
                      Add Content
                    </Button>
                  </div>
                </div>

                    {/* Search & filters - for content view and when parent has content */}
                    {(viewMode === 'content' || viewMode === 'subcategories') && (
                      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                        <Input
                          type="text"
                          placeholder="Search content..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchParams(prev => { prev.set('search', e.target.value); prev.set('page', '1'); return prev; }, { replace: true });
                          }}
                          className="form-control"
                          style={{ maxWidth: 240 }}
                        />
                        <Input
                          type="select"
                          value={contentTypeFilter}
                          onChange={(e) => {
                            setSearchParams(prev => { prev.set('type', e.target.value); prev.set('page', '1'); return prev; }, { replace: true });
                          }}
                          className="form-control"
                          style={{ maxWidth: 140 }}
                        >
                          <option value="all">All formats</option>
                          <option value="single">Single only</option>
                          <option value="module">Module only</option>
                        </Input>
                      </div>
                    )}

                    {/* Content Table or Subcategories Table */}
                    {loadingContent ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading...</p>
                      </div>
                    ) : viewMode === 'subcategories' ? (
                      <>
                        {/* Content in this category (direct + subcategories) */}
                        <h6 className="mb-2">
                          <i className="ri-file-list-3-line me-1"></i>
                          Content in this category
                        </h6>
                        {contents.length > 0 ? (
                          <div className="table-responsive mb-4">
                            <Table className="table-nowrap table-striped align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Title</th>
                                  <th>Format</th>
                                  <th>Status</th>
                                  <th>Duration</th>
                                  <th>Created</th>
                                  <th className="text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {contents.map((content) => (
                                  <tr
                                    key={content._id}
                                    onClick={() => handleViewContent(content)}
                                    style={{ cursor: 'pointer' }}
                                    title="Click to view details"
                                  >
                                    <td>
                                      <div className="d-flex align-items-center">
                                        {content.thumbnail && (
                                          <img
                                            src={content.thumbnail}
                                            alt={content.title}
                                            className="rounded me-2"
                                            style={{ width: '40px', height: '40px', objectFit: 'cover', flexShrink: 0 }}
                                          />
                                        )}
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div className="fw-medium text-truncate" style={{ maxWidth: '250px' }} title={content.title}>
                                            {content.title}
                                          </div>
                                          {content.shortDesc && (
                                            <small className="text-muted text-truncate d-block" style={{ maxWidth: '250px' }} title={content.shortDesc}>
                                              {content.shortDesc}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      {content.modules?.length ? (
                                        <Badge color="secondary">
                                          <i className="ri-stack-line me-1"></i>
                                          Module ({content.modules.length}d)
                                        </Badge>
                                      ) : (
                                        <Badge color="light" className="text-dark border">
                                          Single
                                        </Badge>
                                      )}
                                    </td>
                                    <td>{getStatusBadge(content.status || 'draft')}</td>
                                    <td>{content.duration ? `${Math.floor(content.duration / 60)}:${(content.duration % 60).toString().padStart(2, '0')}` : '-'}</td>
                                    <td>{formatUkDate(content.createdAt)}</td>
                                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                      <div className="d-flex gap-1 justify-content-end">
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-primary"
                                          onClick={(e) => { e.stopPropagation(); handleViewContent(content); }}
                                          title="View Details"
                                        >
                                          <i className="ri-eye-line"></i>
                                        </button>
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-info"
                                          onClick={(e) => { e.stopPropagation(); handleEditContent(content); }}
                                          title="Edit"
                                        >
                                          <i className="ri-edit-2-line"></i>
                                        </button>
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-danger"
                                          onClick={(e) => { e.stopPropagation(); handleDeleteContent(content); }}
                                          title="Delete"
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-muted mb-4">No content in this category yet. Add content or open a subcategory.</p>
                        )}

                        {/* Subcategories Table */}
                        <h6 className="mb-2 mt-3">
                          <i className="ri-folder-open-line me-1"></i>
                          Subcategories
                        </h6>
                        <div className="table-responsive">
                          <Table className="table-nowrap table-striped align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>Subcategory Name</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th className="text-end">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subcategories.length > 0 ? (
                                subcategories.map((subcat) => (
                                  <tr key={subcat._id} onClick={() => navigate(`/dashboard/content/${subcat._id}`)} style={{ cursor: 'pointer' }}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        {subcat.image && (
                                          <img
                                            src={subcat.image}
                                            alt={subcat.name}
                                            className="rounded me-2"
                                            style={{ width: '40px', height: '40px', objectFit: 'cover', flexShrink: 0 }}
                                          />
                                        )}
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div className="fw-medium text-truncate" style={{ maxWidth: '250px' }} title={subcat.name}>
                                            {subcat.name}
                                          </div>
                                          {subcat.shortDesc && (
                                            <small className="text-muted text-truncate d-block" style={{ maxWidth: '250px' }} title={subcat.shortDesc}>
                                              {subcat.shortDesc}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="text-truncate" style={{ maxWidth: '200px' }} title={subcat.description || '-'}>
                                        {subcat.description || '-'}
                                      </div>
                                    </td>
                                    <td>
                                      <Badge color={subcat.isActive ? 'success' : 'secondary'}>
                                        {subcat.isActive ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </td>
                                    <td>{formatUkDate(subcat.createdAt)}</td>
                                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                      <div className="d-flex gap-1 justify-content-end">
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-info"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditCategory(subcat);
                                          }}
                                          title="Edit"
                                        >
                                          <i className="ri-edit-2-line"></i>
                                        </button>
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-danger"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteCategory(subcat);
                                          }}
                                          title="Delete"
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={5} className="text-center py-5">
                                    <i className="ri-folder-open-line fs-1 text-muted"></i>
                                    <p className="mt-2 mb-0 text-muted">No subcategories found</p>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Content Table */}
                        <div className="table-responsive">
                          <Table className="table-nowrap table-striped align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>Title</th>
                                <th>Format</th>
                                <th>Status</th>
                                <th>Duration</th>
                                <th>Created</th>
                                <th className="text-end">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {contents.length > 0 ? (
                                contents.map((content) => (
                                  <tr 
                                    key={content._id} 
                                    onClick={() => handleViewContent(content)}
                                    style={{ cursor: 'pointer' }}
                                    title="Click to view details"
                                  >
                                    <td>
                                      <div className="d-flex align-items-center">
                                        {content.thumbnail && (
                                          <img
                                            src={content.thumbnail}
                                            alt={content.title}
                                            className="rounded me-2"
                                            style={{ width: '40px', height: '40px', objectFit: 'cover', flexShrink: 0 }}
                                          />
                                        )}
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div className="fw-medium text-truncate" style={{ maxWidth: '250px' }} title={content.title}>
                                            {content.title}
                                          </div>
                                          {content.shortDesc && (
                                            <small className="text-muted text-truncate d-block" style={{ maxWidth: '250px' }} title={content.shortDesc}>
                                              {content.shortDesc}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      {content.modules?.length ? (
                                        <Badge color="secondary">
                                          <i className="ri-stack-line me-1"></i>
                                          Module ({content.modules.length}d)
                                        </Badge>
                                      ) : (
                                        <Badge color="light" className="text-dark border">
                                          Single
                                        </Badge>
                                      )}
                                    </td>
                                    <td>{getStatusBadge(content.status || 'draft')}</td>
                                    <td>{content.duration ? `${Math.floor(content.duration / 60)}:${(content.duration % 60).toString().padStart(2, '0')}` : '-'}</td>
                                    <td>{formatUkDate(content.createdAt)}</td>
                                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                      <div className="d-flex gap-1 justify-content-end">
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-primary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewContent(content);
                                          }}
                                          title="View Details"
                                        >
                                          <i className="ri-eye-line"></i>
                                        </button>
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-info"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditContent(content);
                                          }}
                                          title="Edit"
                                        >
                                          <i className="ri-edit-2-line"></i>
                                        </button>
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-danger"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteContent(content);
                                          }}
                                          title="Delete"
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className="text-center py-5">
                                    <i className="ri-file-list-line fs-1 text-muted"></i>
                                    <p className="mt-2 mb-0 text-muted">No content found</p>
                                    <Button color="primary" size="sm" className="mt-3" onClick={handleAddContent}>
                                      <i className="ri-add-line me-1"></i>
                                      Add First Content
                                    </Button>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="pagination-bar">
                        <div className="text-muted">
                          Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, totalContent)} of {totalContent} entries
                        </div>
                        <Pagination className="pagination-rounded mb-0">
                          <PaginationItem disabled={currentPage === 1}>
                            <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                          </PaginationItem>
                          {getVisiblePages().map((page, idx) =>
                            page === 'ellipsis' ? (
                              <PaginationItem disabled key={`ellipsis-${idx}`}>
                                <PaginationLink>…</PaginationLink>
                              </PaginationItem>
                            ) : (
                              <PaginationItem active={page === currentPage} key={page}>
                                <PaginationLink onClick={() => handlePageChange(page)}>{page}</PaginationLink>
                              </PaginationItem>
                            )
                          )}
                          <PaginationItem disabled={currentPage === totalPages}>
                            <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    )}
                  </CardBody>
                </Card>
              ) : (
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-4">All Parent Categories</h5>
                    {loadingCategories ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading categories...</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-nowrap table-striped align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Category Name</th>
                              <th>Description</th>
                              <th>Subcategories</th>
                              <th>Status</th>
                              <th>Created</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parentCategories.length > 0 ? (
                              parentCategories.map((parent) => {
                                const subCount = getSubcategories(parent._id).length;
                                return (
                                  <tr 
                                    key={parent._id}
                                    onClick={() => navigate(`/dashboard/content/${parent._id}`)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <td>
                                      <div className="d-flex align-items-center">
                                        {parent.image && (
                                          <img
                                            src={parent.image}
                                            alt={parent.name}
                                            className="rounded me-2"
                                            style={{ width: '40px', height: '40px', objectFit: 'cover', flexShrink: 0 }}
                                          />
                                        )}
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div className="fw-medium text-truncate" style={{ maxWidth: '200px' }} title={parent.name}>
                                            {parent.name}
                                          </div>
                                          {parent.shortDesc && (
                                            <small className="text-muted text-truncate d-block" style={{ maxWidth: '200px' }} title={parent.shortDesc}>
                                              {parent.shortDesc}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="text-truncate" style={{ maxWidth: '250px' }} title={parent.description || '-'}>
                                        {parent.description || '-'}
                                      </div>
                                    </td>
                                    <td>
                                      <Badge color="info" className="fs-6">
                                        <i className="ri-folder-line me-1"></i>
                                        {subCount} {subCount === 1 ? 'Subcategory' : 'Subcategories'}
                                      </Badge>
                                    </td>
                                    <td>
                                      <Badge color={parent.isActive ? 'success' : 'secondary'}>
                                        {parent.isActive ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </td>
                                    <td>{formatUkDate(parent.createdAt)}</td>
                                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                      <div className="d-flex gap-1 justify-content-end">
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-info"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditCategory(parent);
                                          }}
                                          title="Edit"
                                        >
                                          <i className="ri-edit-2-line"></i>
                                        </button>
                                        <button
                                          className="btn btn-sm btn-icon btn-soft-danger"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteCategory(parent);
                                          }}
                                          title="Delete"
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={6} className="text-center py-5">
                                  <i className="ri-folder-open-line fs-1 text-muted"></i>
                                  <p className="mt-2 mb-0 text-muted">No parent categories found</p>
                                  <Button color="success" size="sm" className="mt-3" onClick={handleAddCategory}>
                                    <i className="ri-add-circle-line me-1"></i>
                                    Add First Parent Category
                                  </Button>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
        </Container>
      </div>

      {/* Category Modal */}
      <Modal isOpen={categoryModal} toggle={() => setCategoryModal(false)} size="lg">
        <ModalHeader toggle={() => setCategoryModal(false)}>
          {editingCategory?._id ? 'Edit Category' : 'Add Category'}
        </ModalHeader>
        <ModalBody>
          <CategoryForm
            category={editingCategory}
            onSave={handleCategorySaved}
            onCancel={() => setCategoryModal(false)}
          />
        </ModalBody>
      </Modal>

      <Modal isOpen={orderModal} toggle={() => setOrderModal(false)} size="lg">
        <ModalHeader toggle={() => setOrderModal(false)}>
          {orderMode === 'subcategories' ? 'Set subcategory order' : 'Set content order'}{' '}
          {selectedCategory ? `- ${selectedCategory.name}` : ''}
        </ModalHeader>
        <ModalBody>
          {loadingOrder ? (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          ) : orderedItems.length === 0 ? (
            <p className="text-muted mb-0">
              {orderMode === 'subcategories'
                ? 'No subcategories to reorder.'
                : 'No content assigned directly to this category.'}
            </p>
          ) : (
            <>
              <SortableVerticalList
                itemIds={orderedItems.map((x) => x._id)}
                onReorder={handleReorderIds}
                renderRow={(id) => {
                  const row = orderedItems.find((x) => x._id === id);
                  if (!row) return null;
                  return (
                    <>
                      <span className="fw-medium text-break">{row.title}</span>
                      <code className="small text-muted ms-auto flex-shrink-0">{row._id}</code>
                    </>
                  );
                }}
              />
              <div className="d-flex gap-2 mt-3">
                <Button color="primary" onClick={handleSaveOrder} disabled={savingOrder}>
                  {savingOrder ? 'Saving...' : 'Save Order'}
                </Button>
                <Button color="secondary" outline onClick={handleClearOrder} disabled={savingOrder}>
                  {orderMode === 'subcategories' ? 'Reset subcategory order' : 'Clear custom content order'}
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal.show}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setDeleteModal({ show: false, type: 'content', id: '', name: '' })}
      />

      {/* Content Detail Modal */}
      <ContentDetailModal
        content={viewingContent}
        isOpen={detailModal}
        onClose={() => setDetailModal(false)}
        onEdit={handleEditFromDetail}
      />
    </React.Fragment>
  );
};

export default ContentPage;
